import { Check, Plus } from 'lucide-react'
import { clsx } from 'clsx'
import { Button } from './Button'

/** State of a supplier listing / product from the parent's perspective. */
export type AddToCartState =
  | 'add'
  | 'update'
  | 'added'
  | 'out-of-stock'
  | 'unavailable'

interface AddToCartButtonProps {
  /** Current listing/cart state — controls label, icon, and disabled state. */
  state: AddToCartState
  /** Fired when the button is pressed. Ignored when disabled. */
  onClick: () => void
  /** Extra classes (e.g. layout: `w-full`, `flex-1`). */
  className?: string
  /** Button size — passed through to the underlying `<Button>` primitive. */
  size?: 'sm' | 'md' | 'lg'
  /** Force-disable even when the state would otherwise be interactive. */
  disabled?: boolean
  /** Hide the leading icon (used in tight layouts). */
  hideIcon?: boolean
}

/**
 * Unified Add-to-Cart button (DEV-48214 §5.4C).
 *
 * Replaces the six ad-hoc button instances listed in `docs/new-branding-feature/
 * ticket-analysis-branding.md#3.6` — all now use the tenant's `--brand-primary`
 * theme colour with no hardcoded overrides (the previous `!bg-pink-500`
 * `isBestPrice` override in SupplierListingCard is dropped, per Q5).
 *
 * State → label mapping:
 *   - `add`           → "Add to basket" (plus icon)
 *   - `update`        → "Update basket"
 *   - `added`         → "Added to basket" (check icon, disabled)
 *   - `out-of-stock`  → "Out of stock" (disabled)
 *   - `unavailable`   → "Unavailable" (disabled)
 */
export function AddToCartButton({
  state,
  onClick,
  className,
  size = 'sm',
  disabled = false,
  hideIcon = false,
}: AddToCartButtonProps) {
  const isDisabled =
    disabled ||
    state === 'out-of-stock' ||
    state === 'unavailable' ||
    state === 'added'

  // Outline is the mockup's CTA style for Add / Update — pink border, white
  // fill, brand-primary text. `added` keeps its filled `secondary` look so
  // the confirmed-in-cart state stands apart from the interactive one.
  let label: string
  let icon: React.ReactNode = null
  let variant: 'primary' | 'secondary' | 'outline' = 'outline'

  switch (state) {
    case 'add':
      label = 'Add to basket'
      if (!hideIcon) icon = <Plus className="w-5 h-5" />
      break
    case 'update':
      label = 'Update basket'
      break
    case 'added':
      label = 'Added to basket'
      variant = 'secondary'
      if (!hideIcon) icon = <Check className="w-5 h-5" />
      break
    case 'out-of-stock':
      label = 'Out of stock'
      break
    case 'unavailable':
      label = 'Unavailable'
      break
  }

  return (
    <Button
      onClick={onClick}
      disabled={isDisabled}
      variant={variant}
      size={size}
      className={clsx('inline-flex items-center justify-center gap-1.5', className)}
    >
      {icon}
      {label}
    </Button>
  )
}
